import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link as MuiLink,
  MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { IndirectTaxService } from '../../services/indirectTax.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { Account } from '../../interfaces/accounting.interface';
import { TaxFilingFrequency, TaxReturn, TaxReturnRow } from '../../interfaces/indirectTax.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { depositAccountOptions } from '../../utils/postingAccounts';
import { formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const label = (v?: string | null) => (v ? v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : '');

const JournalLink: React.FC<{ id?: string | null; text?: string }> = ({ id, text = 'View journal' }) =>
  id ? <MuiLink component={RouterLink} to={`/accounting/journal-entries/${id}`}>{text}</MuiLink> : null;

const RowsTable: React.FC<{ title: string; rows: TaxReturnRow[]; note?: string }> = ({ title, rows, note }) => (
  <Paper sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
    <Typography variant="subtitle1">{title}</Typography>
    {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    <Table data-help-section="tax.vatReturn.rows" size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell data-help="tax.vatReturn.col.code">Code</TableCell><TableCell data-help="tax.vatReturn.col.category">Category</TableCell>
          <TableCell data-help="tax.vatReturn.col.base" align="right">Taxable base</TableCell><TableCell data-help="tax.vatReturn.col.tax" align="right">Tax</TableCell><TableCell data-help="tax.vatReturn.col.components">Components</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No entries in this period.</Typography></TableCell></TableRow>}
        {rows.map((r, i) => (
          <TableRow key={`${r.taxCodeId ?? r.label}-${i}`}>
            <TableCell>{r.label}{r.kind === 'LEGACY' && <Chip size="small" label="Legacy" sx={{ ml: 1 }} />}</TableCell>
            <TableCell>{label(r.category)}</TableCell>
            <TableCell align="right">{formatMoney(r.taxableBase)}</TableCell>
            <TableCell align="right">{formatMoney(r.taxAmount)}</TableCell>
            <TableCell>{(r.components ?? []).map(c => `${c.name} ${c.ratePct}% = ${formatMoney(c.taxAmount)}`).join('; ')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const Card: React.FC<{ title: string; value: string; testId: string }> = ({ title, value, testId }) => (
  <Paper sx={{ p: 2, flex: '1 1 200px', maxWidth: 320 }}>
    <Typography variant="caption" color="text.secondary">{title}</Typography>
    <Typography variant="h6" data-testid={testId}>{value}</Typography>
  </Paper>
);

const VatReturnPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;
  const canManage = usePermission(PERMISSIONS.INDIRECT_TAX_MANAGE);

  const [frequency, setFrequency] = useState<TaxFilingFrequency>('MONTHLY');
  const [month, setMonth] = useState(toLocalIsoDate().slice(0, 7)); // yyyy-MM
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [data, setData] = useState<TaxReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);

  const [confirm, setConfirm] = useState<'settle' | 'reopen' | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: '', date: toLocalIsoDate(), bank: '', challanNo: '', challanDate: '' });

  const periodDate = frequency === 'QUARTERLY'
    ? `${year}-${String((quarter - 1) * 3 + 1).padStart(2, '0')}-01`
    : `${month}-01`;

  useEffect(() => {
    IndirectTaxService.getProfile(institutionId)
      .then(p => { if (p?.filingFrequency) setFrequency(p.filingFrequency); })
      .catch(() => undefined)
      .finally(() => setProfileLoaded(true));
    AccountService.getAccountsByType('ASSET').then(a => setAccounts(a ?? [])).catch(() => undefined);
  }, [institutionId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await IndirectTaxService.getReturn(institutionId, periodDate));
    } catch (e) {
      setData(null);
      toast.showApiError(e, 'Failed to load VAT return');
    } finally {
      setLoading(false);
    }
  }, [institutionId, periodDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (profileLoaded) load(); }, [profileLoaded, load]);

  const settlement = data?.settlement ?? null;
  const settled = settlement?.status === 'SETTLED';
  const hasPayments = (settlement?.payments?.length ?? 0) > 0;
  const canPay = canManage && settled && (settlement?.netAmount ?? 0) > 0 && (settlement?.outstandingAmount ?? 0) > 0;

  const run = async (fn: () => Promise<unknown>, okMsg: string, failMsg: string) => {
    try {
      setBusy(true);
      await fn();
      toast.showSuccess(okMsg);
      setConfirm(null);
      setPayOpen(false);
      await load();
    } catch (e) {
      setConfirm(null);
      toast.showApiError(e, failMsg);
    } finally {
      setBusy(false);
    }
  };

  const openPay = () => {
    setPay({ amount: String(settlement?.outstandingAmount ?? ''), date: toLocalIsoDate(), bank: '', challanNo: '', challanDate: '' });
    setPayOpen(true);
  };

  const submitPay = () => run(() => IndirectTaxService.recordPayment({
    settlementId: settlement!.id, paymentDate: pay.date, amount: Number(pay.amount), bankAccountId: pay.bank,
    challanNo: pay.challanNo.trim(), challanDate: pay.challanDate,
  }), 'VAT payment recorded', 'Failed to record VAT payment');

  const payValid = Number(pay.amount) > 0 && !!pay.bank && !!pay.date && !!pay.challanNo.trim() && !!pay.challanDate;

  const net = data?.netPayable ?? 0;
  const split = data?.split ?? null;

  return (
    <Box data-help-page="tax.vatReturn.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>VAT Return</Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        {frequency === 'QUARTERLY' ? (
          <>
            <Box sx={{ flex: '1 1 120px', maxWidth: 160 }}>
              <TextField data-help="tax.vatReturn.year" fullWidth size="small" type="number" label="Year" value={year}
                onChange={e => setYear(Number(e.target.value) || year)} />
            </Box>
            <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
              <TextField data-help="tax.vatReturn.quarter" select fullWidth size="small" label="Quarter" value={quarter} onChange={e => setQuarter(Number(e.target.value))}>
                {[1, 2, 3, 4].map(q => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
              </TextField>
            </Box>
          </>
        ) : (
          <Box sx={{ flex: '1 1 180px', maxWidth: 240 }}>
            <TextField data-help="tax.vatReturn.period" fullWidth size="small" type="month" label="Period" value={month} InputLabelProps={{ shrink: true }}
              onChange={e => e.target.value && setMonth(e.target.value)} />
          </Box>
        )}
        {data && (
          <Typography variant="body2" color="text.secondary">
            {formatDate(data.periodStart)} to {formatDate(data.periodEnd)} ({label(data.filingFrequency)}, {label(data.regime)})
          </Typography>
        )}
      </Box>

      {loading && <CircularProgress />}

      {!loading && data && (
        <>
          {split ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Card title="Output VAT" value={formatMoney(split.outputVat)} testId="vat-output" />
              <Card title="Input VAT (recoverable)" value={formatMoney(split.recoverableInputVat)} testId="vat-input" />
              <Card title={split.netVat < 0 ? 'Net VAT refundable (carried forward)' : 'Net VAT payable'}
                value={formatMoney(Math.abs(split.netVat))} testId="vat-net" />
              <Card title="Supplementary duty payable" value={formatMoney(split.sdPayable)} testId="vat-sd" />
              {split.otherPayable !== 0 && <Card title="Other duties payable" value={formatMoney(split.otherPayable)} testId="vat-other" />}
              <Card title="Total payable" value={formatMoney(split.totalPayable)} testId="vat-total" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Card title="Output tax" value={formatMoney(data.outputTax)} testId="vat-output" />
              <Card title="Recoverable input tax" value={formatMoney(data.recoverableInputTax)} testId="vat-input" />
              <Card title={net < 0 ? 'Net refundable' : 'Net payable'} value={formatMoney(Math.abs(net))} testId="vat-net" />
            </Box>
          )}
          {split && split.vatRefundable > 0 && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="vat-credit-note">
              Input VAT exceeds output VAT by {formatMoney(split.vatRefundable)}. That credit is carried forward and never
              reduces Supplementary Duty{split.sdPayable > 0 ? `, which is still payable in full (${formatMoney(split.sdPayable)})` : ''}.
            </Alert>
          )}

          {data.changedSinceSettlement && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Documents in this period changed after it was settled. The figures below no longer match the settlement journal.
            </Alert>
          )}

          <RowsTable title="Output tax by code" rows={data.output}
            note={data.legacyOutputTax ? `Includes ${formatMoney(data.legacyOutputTax)} on documents without a tax code (legacy rate buckets).` : undefined} />
          <RowsTable title="Input tax - recoverable" rows={data.inputRecoverable}
            note={data.legacyInputTax ? `Includes ${formatMoney(data.legacyInputTax)} on documents without a tax code.` : undefined} />
          <RowsTable title="Input tax - non-recoverable (information only, booked as cost)" rows={data.inputNonRecoverable} />

          <Paper sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
            <Typography variant="subtitle1">Output by category</Typography>
            <Table data-help-section="tax.vatReturn.byCategory" size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow><TableCell data-help="tax.vatReturn.col.category">Category</TableCell><TableCell data-help="tax.vatReturn.col.base" align="right">Taxable base</TableCell><TableCell data-help="tax.vatReturn.col.tax" align="right">Tax</TableCell></TableRow></TableHead>
              <TableBody>
                {data.outputByCategory.map(c => (
                  <TableRow key={c.category}>
                    <TableCell>{label(c.category)}</TableCell>
                    <TableCell align="right">{formatMoney(c.taxableBase)}</TableCell>
                    <TableCell align="right">{formatMoney(c.taxAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper data-help-section="tax.vatReturn.settlement" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: '1 1 200px' }}>Settlement</Typography>
              {settlement && <Chip size="small" label={settlement.status === 'SETTLED' ? 'Settled' : 'Reopened'}
                color={settled ? 'success' : 'warning'} />}
              {data.changedSinceSettlement && <Chip size="small" color="warning" label="Changed since settlement" />}
            </Box>

            {!settlement && <Typography color="text.secondary">This period has not been settled.</Typography>}
            {settlement && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  Net {settlement.netAmount < 0 ? 'refundable' : 'payable'}: {formatMoney(Math.abs(settlement.netAmount))}
                  {' '}| Paid: {formatMoney(settlement.paidAmount)} | Outstanding: {formatMoney(settlement.outstandingAmount)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <JournalLink id={settlement.journalEntryId} text="Settlement journal" />
                  <JournalLink id={settlement.reversalJournalEntryId} text="Reversal journal" />
                </Box>
              </Box>
            )}

            {canManage && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {(!settlement || settlement.status === 'REOPENED') && (
                  <Button data-help="tax.vatReturn.action.settle" variant="contained" onClick={() => setConfirm('settle')} disabled={busy}>Settle period</Button>
                )}
                {settled && !hasPayments && (
                  <Button data-help="tax.vatReturn.action.reopen" variant="outlined" color="warning" onClick={() => setConfirm('reopen')} disabled={busy}>Reopen</Button>
                )}
                {canPay && <Button data-help="tax.vatReturn.action.pay" variant="contained" onClick={openPay} disabled={busy}>Record VAT payment</Button>}
              </Box>
            )}

            {settlement && settlement.payments.length > 0 && (
              <Table data-help-section="tax.vatReturn.payments" size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell data-help="tax.vatReturn.pay.col.date">Date</TableCell><TableCell data-help="tax.vatReturn.pay.col.amount" align="right">Amount</TableCell><TableCell data-help="tax.vatReturn.pay.col.challanNo">Challan no.</TableCell>
                    <TableCell data-help="tax.vatReturn.pay.col.challanDate">Challan date</TableCell><TableCell data-help="tax.vatReturn.pay.col.journal">Journal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settlement.payments.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{formatDate(p.paymentDate)}</TableCell>
                      <TableCell align="right">{formatMoney(p.amount)}</TableCell>
                      <TableCell>{p.challanNo ?? ''}</TableCell>
                      <TableCell>{p.challanDate ? formatDate(p.challanDate) : ''}</TableCell>
                      <TableCell><JournalLink id={p.journalEntryId} text="View" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}

      <ConfirmDialog
        open={confirm === 'settle'}
        title="Settle VAT period"
        message={split
          ? `This posts the settlement journal for the whole filing period: output VAT against recoverable input VAT (${split.netVat < 0 ? 'refundable ' : 'payable '}${formatMoney(Math.abs(split.netVat))}), plus Supplementary Duty${split.otherPayable !== 0 ? ' and other duties' : ''} cleared in full (${formatMoney(split.sdPayable + split.otherPayable)}). Total payable: ${formatMoney(split.totalPayable)}.`
          : 'This posts the settlement journal for the whole filing period (output tax against recoverable input tax) and records the net amount payable or refundable.'}
        confirmLabel="Settle"
        submitting={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => run(() => IndirectTaxService.settle(institutionId, periodDate), 'Period settled', 'Failed to settle period')}
      />
      <ConfirmDialog
        open={confirm === 'reopen'}
        title="Reopen VAT period"
        message="This reverses the settlement journal so the period can be corrected and settled again."
        confirmLabel="Reopen"
        confirmColor="warning"
        submitting={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => run(() => IndirectTaxService.reopen(settlement!.id), 'Period reopened', 'Failed to reopen period')}
      />

      <Dialog data-help-page="tax.vatReturn.pay.page" open={payOpen} onClose={() => setPayOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record VAT payment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField data-help="tax.vatReturn.pay.field.amount" fullWidth size="small" required type="number" label="Amount" value={pay.amount}
            onChange={e => setPay(p => ({ ...p, amount: e.target.value }))} />
          <TextField data-help="tax.vatReturn.pay.field.date" fullWidth size="small" required type="date" label="Payment date" value={pay.date} InputLabelProps={{ shrink: true }}
            onChange={e => setPay(p => ({ ...p, date: e.target.value }))} />
          <AccountAutocomplete data-help="tax.vatReturn.pay.field.bank" accounts={depositAccountOptions(accounts)} value={pay.bank} onChange={id => setPay(p => ({ ...p, bank: id }))}
            label="Bank / cash account" required />
          <TextField data-help="tax.vatReturn.pay.field.challanNo" fullWidth size="small" required label="Challan no." value={pay.challanNo} inputProps={{ maxLength: 64 }} onChange={e => setPay(p => ({ ...p, challanNo: e.target.value }))} />
          <TextField data-help="tax.vatReturn.pay.field.challanDate" fullWidth size="small" required type="date" label="Challan date" value={pay.challanDate} InputLabelProps={{ shrink: true }}
            onChange={e => setPay(p => ({ ...p, challanDate: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button data-help="tax.vatReturn.pay.cancel" onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button data-help="tax.vatReturn.pay.submit" variant="contained" onClick={submitPay} disabled={!payValid || busy}>Record payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VatReturnPage;
